import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ManualContactModal from '../ui/ManualContactModal';

import { contactSyncService } from '../../services/contactSync';
import { oauthService } from '../../services/oauthService';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { unifiedContactService } from '../../services/unifiedContactService';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';

import { API_BASE_URL } from '../../config/api';
import { RELATIONSHIP_OPTIONS } from '../../constants/relationshipOptions';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, ResponsiveFooter } from '../ui';
import { PeopleIcon, ImportantDocumentsIcon } from '../ui/Icons';
import { SubHeadline } from '../ui/Typography';
import { normalizeDocumentUrl } from '../../utils/documentUrl';

type ContactFilter = 'all' | 'google' | 'outlook' | 'csv' | 'manual';

const Frame137: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const firstName =
    (location.state as { firstName?: string } | null)?.firstName || 'Your';

  const { session, updateTaskProgress } = useSessionPersistence();

  // UI state
  const [googleSyncing, setGoogleSyncing] = useState(false);
  const [outlookSyncing] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showOutlookCsvModal, setShowOutlookCsvModal] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const outlookFileInputRef = useRef<HTMLInputElement>(null);

  // Contact management state
  const [allContacts, setAllContacts] = useState<UnifiedContact[]>([]);
  const [showContactManagement, setShowContactManagement] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<ContactFilter>('all');
  const [editingContact, setEditingContact] = useState<UnifiedContact | null>(null);
  const [contactsLoading, setContactsLoading] = useState(false);

  // --------------------------
  // Helpers: task persistence
  // --------------------------

  const computeServicesFromContacts = useCallback((contacts: UnifiedContact[]) => {
    const set = new Set<'google' | 'outlook' | 'csv' | 'manual'>();

    for (const c of contacts) {
      if (c.source === 'google') set.add('google');
      if (c.source === 'outlook') set.add('outlook');
      if (c.source === 'csv') set.add('csv');
      if (c.source === 'manual') set.add('manual');
    }

    return Array.from(set);
  }, []);

  const persistContactsTask = useCallback(
    async (contacts: UnifiedContact[]) => {
      const services = computeServicesFromContacts(contacts);
      const contactsCount = contacts.length;

      await updateTaskProgress(
        'contacts',
        { services, contactsCount },
        contactsCount > 0 ? 'in-progress' : 'not-started'
      );
    },
    [computeServicesFromContacts, updateTaskProgress]
  );

  const refreshContactsAndPersist = useCallback(async () => {
    const contacts = await unifiedContactService.getAllContacts();
    setAllContacts(contacts);
    await persistContactsTask(contacts);
    return contacts;
  }, [persistContactsTask]);

  // --------------------------
  // Initial load
  // --------------------------

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setContactsLoading(true);
      try {
        const contacts = await unifiedContactService.getAllContacts();
        if (cancelled) return;

        setAllContacts(contacts);
        await persistContactsTask(contacts);
      } catch (error) {
        console.error('[Frame137] Error loading contacts:', error);
      } finally {
        if (!cancelled) setContactsLoading(false);
      }
    };

    void loadData();

    return () => {
      cancelled = true;
    };
  }, [persistContactsTask]);

  // --------------------------
  // Derived UI data
  // --------------------------

  const filterCounts = useMemo(() => {
    const counts: Record<ContactFilter, number> = {
      all: allContacts.length,
      google: 0,
      outlook: 0,
      csv: 0,
      manual: 0,
    };

    for (const c of allContacts) {
      if (c.source === 'google') counts.google += 1;
      if (c.source === 'outlook') counts.outlook += 1;
      if (c.source === 'csv') counts.csv += 1;
      if (c.source === 'manual') counts.manual += 1;
    }

    return counts;
  }, [allContacts]);

  const visibleContacts = useMemo(() => {
    if (selectedFilter === 'all') return allContacts;
    return allContacts.filter((c) => c.source === selectedFilter);
  }, [allContacts, selectedFilter]);

  // --------------------------
  // Actions
  // --------------------------

  const handleGoogleSync = useCallback(async () => {
    setGoogleSyncing(true);
    try {
      const userId = session?.id;
      if (!userId) {
        alert('Session not ready yet. Please refresh and try again.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/google?userId=${userId}`);
      const data = await response.json();

      if (data?.url) {
        window.location.href = data.url;
      } else {
        console.error('[Frame137] Failed to get auth URL:', data);
        alert('Failed to start Google sync. Please try again.');
      }
    } catch (error) {
      console.error('[Frame137] Failed to start Google sync:', error);
      alert('Failed to start Google sync. Please try again.');
    } finally {
      setGoogleSyncing(false);
    }
  }, [session?.id]);

  const handleOutlookSync = useCallback(() => {
    setShowOutlookCsvModal(true);
  }, []);

  const handleManualContactSubmit = useCallback(
    async (contact: unknown) => {
      setManualSubmitting(true);
      try {
        if (editingContact) {
          await unifiedContactService.updateContact(editingContact.id, {
            firstName: contact.firstName.trim(),
            lastName: contact.lastName.trim(),
            email: contact.email?.trim() || '',
            phone: contact.phone?.trim(),
            relationship: contact.relationship?.toLowerCase().trim(),
            notes: contact.notes?.trim(),
          });
          setEditingContact(null);
        } else {
          await unifiedContactService.createContact({
            firstName: contact.firstName.trim(),
            lastName: contact.lastName.trim(),
            email: contact.email?.trim() || '',
            phone: contact.phone?.trim(),
            relationship: contact.relationship?.toLowerCase().trim(),
            source: 'manual',
            notes: contact.notes?.trim(),
          });
        }

        await refreshContactsAndPersist();
      } catch (error: unknown) {
        console.error('[Frame137] Failed to save manual contact:', error);
        if (error?.code === 'DUPLICATE_CONTACT') {
          alert(`A contact with email ${contact.email} already exists.`);
        } else {
          alert('Failed to save contact. Please try again.');
        }
        throw error;
      } finally {
        setManualSubmitting(false);
      }
    },
    [editingContact, refreshContactsAndPersist]
  );

  const handleDeleteContact = useCallback(
    async (contactId: string) => {
      if (!confirm('Are you sure you want to delete this contact?')) return;

      try {
        await unifiedContactService.deleteContact(contactId);
        await refreshContactsAndPersist();
      } catch (error) {
        console.error('[Frame137] Failed to delete contact:', error);
        alert('Failed to delete contact. Please try again.');
      }
    },
    [refreshContactsAndPersist]
  );

  const handleEditContact = useCallback((contact: UnifiedContact) => {
    setEditingContact(contact);
    setShowManualModal(true);
  }, []);

  // --------------------------
  // CSV validation + import
  // --------------------------

  const validateContact = useCallback((contact: unknown, lineNumber: number) => {
    const errors: string[] = [];

    if (!contact.firstName?.trim()) errors.push(`Line ${lineNumber}: First name is required`);
    if (!contact.lastName?.trim()) errors.push(`Line ${lineNumber}: Last name is required`);
    if (!contact.email?.trim() && !contact.phone?.trim())
      errors.push(`Line ${lineNumber}: Either email or phone is required`);
    if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email))
      errors.push(`Line ${lineNumber}: Invalid email format`);
    if (!contact.relationship?.trim()) errors.push(`Line ${lineNumber}: Relationship is required`);

    const validRelationships = RELATIONSHIP_OPTIONS.map(r => r.toLowerCase());

    if (
      contact.relationship &&
      !validRelationships.includes(contact.relationship.toLowerCase())
    ) {
      errors.push(
        `Line ${lineNumber}: Invalid relationship. Must be one of: ${validRelationships.join(', ')}`
      );
    }

    return { valid: errors.length === 0, errors };
  }, []);

  const parseCsvText = useCallback((raw: string) => {
    let text = raw;
    if (text.charCodeAt(0) === 0xfeff) text = text.slice(1); // BOM
    text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    const lines = text
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    return lines;
  }, []);

  const importCsvFile = useCallback(
    async (file: File, source: 'csv') => {
      setCsvUploading(true);
      try {
        const lines = parseCsvText(await file.text());
        if (lines.length < 2) {
          alert(
            'CSV file is empty or has no data rows. Please ensure your file has a header row and at least one data row.'
          );
          return;
        }

        const header = lines[0].toLowerCase();
        if (!header.includes('firstname') || !header.includes('lastname')) {
          alert(
            'CSV must include FirstName and LastName columns. Please check the format.\n\nExpected format:\nFirstName,LastName,Relationship,Email,Phone,Notes'
          );
          return;
        }

        const allErrors: string[] = [];
        const validContacts: unknown[] = [];

        lines.slice(1).forEach((line, index) => {
          const [firstName, lastName, relationship, email, phone, notes] = line
            .split(',')
            .map((f) => f.trim());

          const contact = {
            firstName: firstName || '',
            lastName: lastName || '',
            relationship: relationship?.toLowerCase() || 'other',
            email: email || '',
            phone: phone || '',
            notes: notes || '',
          };

          const validation = validateContact(contact, index + 2);
          if (validation.valid) validContacts.push(contact);
          else allErrors.push(...validation.errors);
        });

        if (allErrors.length > 0) {
          const msg =
            `Found ${allErrors.length} validation error(s):\n\n` +
            `${allErrors.slice(0, 5).join('\n')}` +
            (allErrors.length > 5 ? `\n\n...and ${allErrors.length - 5} more errors` : '');

          if (validContacts.length === 0) {
            alert(msg + '\n\nNo valid contacts found. Please fix the errors and try again.');
            return;
          }

          const proceed = confirm(
            msg + `\n\nFound ${validContacts.length} valid contact(s). Import valid contacts and skip invalid ones?`
          );
          if (!proceed) return;
        }

        const result = await unifiedContactService.bulkImport(validContacts, source);

        let message = `Successfully imported ${result.imported} contact(s)`;
        if (result.duplicates > 0) message += `, skipped ${result.duplicates} duplicate(s)`;
        if (result.errors > 0) message += `, ${result.errors} error(s) encountered`;
        alert(message);

        await refreshContactsAndPersist();
      } catch (error) {
        console.error('[Frame137] Failed to import CSV:', error);
        alert('Failed to import CSV file. Please ensure it follows the correct format.');
      } finally {
        setCsvUploading(false);
      }
    },
    [parseCsvText, refreshContactsAndPersist, validateContact]
  );

  const handleCsvUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      await importCsvFile(file, 'csv');

      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [importCsvFile]
  );

  const handleOutlookCsvUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Still stored as 'csv' contacts (matches your current logic)
      await importCsvFile(file, 'csv');

      setShowOutlookCsvModal(false);
      if (outlookFileInputRef.current) outlookFileInputRef.current.value = '';
    },
    [importCsvFile]
  );

  // --------------------------
  // OAuth callback handler
  // --------------------------

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const authProvider = params.get('auth') as 'google' | 'microsoft' | null;
      const success = params.get('success') === 'true';
      const error = params.get('error');
      const oauthSessionId = params.get('sessionId');

      if (!authProvider) return;

      // Always strip params after processing
      const cleanupUrl = () =>
        window.history.replaceState({}, document.title, window.location.pathname);

      if (!success) {
        console.error(`❌ ${authProvider} OAuth failed:`, error);
        alert(`Failed to authorize ${authProvider}. Please try again.`);
        cleanupUrl();
        return;
      }

      try {
        const sessionIdToUse = oauthSessionId || session?.id;
        if (!sessionIdToUse) {
          alert('Session not ready yet. Please refresh and try again.');
          cleanupUrl();
          return;
        }

        const tokenResponse = await oauthService.getAccessToken(authProvider, sessionIdToUse);

        if (authProvider === 'google') {
          const result = await contactSyncService.syncGoogleContacts(tokenResponse);
          if (!result.success) {
            alert(`Failed to sync contacts: ${result.message}`);
            cleanupUrl();
            return;
          }
          alert(`Successfully synced ${result.contacts?.length ?? 0} Google contacts!`);
        }

        if (authProvider === 'microsoft') {
          // not implemented
        }

        await refreshContactsAndPersist();
        cleanupUrl();
      } catch (err) {
        console.error(`❌ Failed to sync ${authProvider} contacts:`, err);
        alert('Failed to sync contacts. Please try again.');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    void handleCallback();
  }, [session?.id, refreshContactsAndPersist]);

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Planning your estate is a family affair." />

        <div className="mt-6">
          <SubHeadline>
            Sync your contact list with End of an Era, and pull in your loved ones&apos; details instantly.
          </SubHeadline>
        </div>

        {/* Sync cards */}
        <div
          className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 self-start"
          style={{ marginLeft: '0px' }}
        >
          {/* Gmail */}
          <button
            onClick={handleGoogleSync}
            disabled={googleSyncing}
            className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left"
          >
            <div className="flex items-center gap-4">
              <svg className="w-8 h-8 flex-shrink-0" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M24 24L4 10v28h8V21.5l12 8 12-8V38h8V10z" />
                <path fill="#34A853" d="M4 10l20 14 20-14H4z" opacity=".15" />
              </svg>
              <div>
                <h3 className="font-medium text-stone-800 leading-tight">Gmail</h3>
                <p className="text-sm text-stone-600 mt-1">
                  {googleSyncing ? 'Syncing...' : `Sync Gmail contacts${filterCounts.google > 0 ? ` (${filterCounts.google} synced)` : ''}`}
                </p>
              </div>
            </div>
          </button>

          {/* Outlook */}
          <button
            onClick={handleOutlookSync}
            disabled={outlookSyncing}
            className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left"
          >
            <div className="flex items-center gap-4">
              <svg className="w-8 h-8 flex-shrink-0" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#0078D4" d="M6 12h24v24H6z" />
                <path fill="#005A9E" d="M30 16l12 4v20H30z" />
                <path fill="#fff" d="M12 18h12v12H12z" />
                <circle cx="18" cy="24" r="5" fill="#0078D4" />
              </svg>
              <div>
                <h3 className="font-medium text-stone-800 leading-tight">Outlook</h3>
                <p className="text-sm text-stone-600 mt-1">
                  {outlookSyncing ? 'Syncing...' : `Sync Outlook contacts${filterCounts.outlook + filterCounts.csv > 0 ? ` (${filterCounts.outlook + filterCounts.csv} synced)` : ''}`}
                </p>
              </div>
            </div>
          </button>

          {/* CSV Upload */}
          <div className="relative group">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={csvUploading}
              className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left w-full"
            >
              <div className="flex items-center gap-4">
                <svg className="w-8 h-8 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="#16A34A" fillOpacity="0.1" />
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M14 2v6h6M10 12h4M10 16h4M10 20h4" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div>
                  <h3 className="font-medium text-stone-800 leading-tight">CSV File</h3>
                  <p className="text-sm text-stone-600 mt-1">{csvUploading ? 'Uploading...' : 'Upload CSV file'}</p>
                </div>
              </div>
            </button>

            <div
              className="
                absolute left-1/2 top-full mt-2
                -translate-x-1/2
                w-60
                rounded-lg
                border border-stone-300
                bg-white
                px-3 py-2
                text-xs text-black
                shadow-lg
                opacity-0
                pointer-events-none
                group-hover:opacity-100
                transition-opacity duration-200
                z-50
              "
            >
              <p className="font-mono text-center">
                FirstName, LastName, Relationship,
                <br />
                Email, Phone, Notes
              </p>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={handleCsvUpload}
              className="hidden"
            />
          </div>

          {/* Manual Entry */}
          <button
            onClick={() => setShowManualModal(true)}
            className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left"
          >
            <div className="flex items-center gap-4">
              <svg className="w-8 h-8 text-cyan-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <div>
                <h3 className="font-medium text-stone-800 leading-tight">Enter manually</h3>
                <p className="text-sm text-stone-600 mt-1">Add contact manually</p>
              </div>
            </div>
          </button>
        </div>

        {/* Contact Management Section */}
        {allContacts.length > 0 && (
          <div className="mt-10 max-w-4xl w-full self-start" style={{ marginLeft: '0px' }}>
            <div className="bg-white rounded-lg border border-stone-300 shadow-sm">
              <div
                className="p-4 border-b border-stone-200 flex items-center justify-between cursor-pointer hover:bg-stone-50 transition-colors"
                onClick={() => setShowContactManagement((v) => !v)}
              >
                <div>
                  <h3 className="font-sans text-lg font-semibold text-stone-800">
                    Your Contacts ({allContacts.length})
                  </h3>
                  <p className="text-sm text-stone-600 mt-1">View and manage your imported contacts</p>
                </div>
                <svg
                  className={`w-5 h-5 text-stone-600 transition-transform ${showContactManagement ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {showContactManagement && (
                <div className="p-4">
                  <div className="flex gap-2 mb-4 overflow-x-auto">
                    {(['all', 'google', 'outlook', 'csv', 'manual'] as ContactFilter[]).map((filter) => (
                      <button
                        key={filter}
                        onClick={() => setSelectedFilter(filter)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                          selectedFilter === filter
                            ? 'bg-cyan-700 text-white'
                            : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                        }`}
                      >
                        {filter.charAt(0).toUpperCase() + filter.slice(1)} ({filterCounts[filter]})
                      </button>
                    ))}
                  </div>

                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {contactsLoading ? (
                      <div className="text-center py-8 text-stone-500">
                        <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                        <p>Loading contacts...</p>
                      </div>
                    ) : (
                      <>
                        {visibleContacts.map((contact) => {
                          const metadataParts: string[] = [];
                          if (contact.email) metadataParts.push(contact.email);
                          if (contact.phone) metadataParts.push(contact.phone);
                          if (contact.relationship) {
                            metadataParts.push(
                              contact.relationship.charAt(0).toUpperCase() + contact.relationship.slice(1)
                            );
                          }
                          if (contact.source) metadataParts.push(contact.source);

                          return (
                            <div
                              key={contact.id}
                              className="flex flex-col gap-2 bg-white rounded-lg border border-neutral-200 p-4"
                            >
                              <div className="flex items-start gap-4">
                                <PeopleIcon className="w-6 h-6 text-cyan-700 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-stone-800">
                                    {contact.firstName} {contact.lastName}
                                  </div>
                                  {metadataParts.length > 0 && (
                                    <div className="text-sm text-stone-500 mt-1">{metadataParts.join(' • ')}</div>
                                  )}
                                  {contact.notes && (
                                    <div className="text-sm text-stone-600 mt-2">{contact.notes}</div>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEditContact(contact)}
                                    className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300 text-sm"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteContact(contact.id)}
                                    className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300 text-sm"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>

                              {/* Document Preview & Download Section */}
                              {/* (contact as unknown) resolves ts(2339) and inline types resolve ts(7006) */}
                              {(contact as unknown).documents && (contact as unknown).documents.length > 0 && (
                                <div className="border-t border-stone-100 mt-3 pt-3">
                                  <p className="text-cyan-600 text-[13px] font-bold mb-2">
                                    {(contact as unknown).documents.length} document{(contact as unknown).documents.length > 1 ? 's' : ''} attached
                                  </p>
                                  <div className="space-y-1">
                                    {(contact as unknown).documents.map((doc: { name: string; url?: string }, idx: number) => (
                                      <div
                                        key={idx}
                                        onClick={() => doc.url && window.open(normalizeDocumentUrl(doc.url), '_blank')}
                                        className="flex items-start gap-3 w-full p-2 rounded-md hover:bg-stone-50 cursor-pointer transition-colors"
                                      >
                                        <ImportantDocumentsIcon className="w-4 h-4 text-cyan-600 opacity-70 mt-0.5 flex-shrink-0" />
                                        <span className="text-stone-800 font-medium text-sm break-all">
                                          {doc.name}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {visibleContacts.length === 0 && (
                          <div className="text-center py-8 text-stone-500">
                            <p>No contacts found for this filter.</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Manual Contact Modal */}
        <ManualContactModal
          open={showManualModal}
          onClose={() => {
            setShowManualModal(false);
            setEditingContact(null);
          }}
          onSubmit={handleManualContactSubmit}
          loading={manualSubmitting}
          initialData={
            editingContact
              ? {
                  firstName: editingContact.firstName,
                  lastName: editingContact.lastName,
                  email: editingContact.email || '',
                  phone: editingContact.phone || '',
                  relationship: editingContact.relationship || '',
                  notes: editingContact.notes || '',
                }
              : undefined
          }
        />

        {/* Outlook CSV Upload Modal */}
        {showOutlookCsvModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full m-4">
              <h2 className="text-xl font-semibold mb-4 text-stone-800">Upload Contacts CSV</h2>

              <div className="space-y-4">
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                  <h3 className="font-semibold text-stone-800 mb-2">Required CSV Format</h3>
                  <p className="text-sm text-stone-700 mb-3">
                    Your CSV file must include a header row with the following columns in this exact order:
                  </p>
                  <div className="bg-white rounded border border-stone-200 p-3 font-mono text-sm">
                    <code className="text-cyan-700">FirstName,LastName,Relationship,Email,Phone,Notes</code>
                  </div>
                  <div className="mt-3 text-sm text-stone-600 space-y-1">
                    <p>
                      <strong>Required:</strong> FirstName, LastName, Relationship, and either Email or Phone
                    </p>
                    <p>
                      <strong>Relationship values:</strong> spouse, child, parent, sibling, friend, attorney,
                      accountant, executor, trustee, beneficiary, or other
                    </p>
                    <p>
                      <strong>Optional:</strong> Notes (any additional information)
                    </p>
                  </div>
                </div>

                <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-stone-700 mb-2">Example CSV format:</p>
                  <pre className="text-xs font-mono text-stone-600 overflow-x-auto">
FirstName,LastName,Relationship,Email,Phone,Notes
John,Doe,spouse,john@email.com,555-123-4567,My spouse
Jane,Smith,attorney,jane@law.com,555-987-6543,Estate attorney
Bob,Johnson,friend,bob@email.com,,Close friend from college
                  </pre>
                </div>

                <p className="text-sm text-stone-600">
                  Upload your contacts CSV file. The system will validate the format and show any errors before importing.
                </p>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setShowOutlookCsvModal(false)}
                    className="px-4 py-2 text-stone-600 hover:text-stone-800 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => outlookFileInputRef.current?.click()}
                    disabled={csvUploading}
                    className="px-6 py-2 bg-cyan-700 text-white rounded-lg font-medium hover:bg-cyan-800 transition-colors disabled:opacity-50"
                  >
                    {csvUploading ? 'Uploading...' : 'Select File'}
                  </button>
                </div>
              </div>
              <input
                type="file"
                ref={outlookFileInputRef}
                accept=".csv"
                onChange={handleOutlookCsvUpload}
                className="hidden"
              />
            </div>
          </div>
        )}
      </ResponsiveContent>

      <ResponsiveFooter
        title="Setup Your Plan"
        subtitle="Add and manage your loved ones"
        progressPercent={50}
        onNext={() => navigate('/frame-138', { state: { firstName }})}
        onBack={() => navigate('/frame-133')}
        nextText="Next"
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame137;